import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { bankingInfoService } from '../../../../services/bankingInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('BankingInfoInput');

const ACCOUNT_TYPES = ['Checking', 'Savings', 'Business Checking', 'Business Savings', 'Money Market', 'Certificate of Deposit'];
const LOCATIONS = ['Digital Vault', 'Safety Deposit Box', 'Home Safe', 'Office Safe', 'Other'];
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const BankingInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { bankingId?: string; isEdit?: boolean }>, string>>();
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedBankingId, setSavedBankingId] = useState<string | null>(null);
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const bankingId = (route.params as any)?.bankingId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  const [formData, setFormData] = useState({
    bankName: '',
    accountType: '',
    accountNumber: '',
    branchAddress: '',
    branchPhone: '',
    onlineBankingUrl: '',
    username: '',
    password: '',
    contactPerson: '',
    contactPhone: '',
    notes: '',
    location: '',
    specialInstructions: '',
    street: '',
    city: '',
    state: '',
    zip: '',
  });

  // Load existing data in edit mode
  useEffect(() => {
    logger.debug('[Edit Debug] UseEffect triggered - isEdit:', isEdit, 'bankingId:', bankingId);
    logger.debug('[Edit Debug] Route params:', route.params);
    
    if (isEdit && bankingId) {
      logger.debug('[Edit Debug] Loading data for edit mode');
      loadBankingData();
    } else {
      logger.debug('[Edit Debug] Not in edit mode or missing bankingId');
    }
  }, [isEdit, bankingId]);

  const loadBankingData = async () => {
    try {
      logger.debug('[Edit Debug] Loading banking data for ID:', bankingId);
      const bankingInfo = await bankingInfoService.getBankingInfo(bankingId!);
      logger.debug('[Edit Debug] Banking info loaded:', bankingInfo);
      
      if (bankingInfo) {
        // Parse address back into components if it exists
        let parsedStreet = '', parsedCity = '', parsedState = '', parsedZip = '';
        if (bankingInfo.branchAddress) {
          // Try to parse the address back into components
          const addressParts = bankingInfo.branchAddress.split(' ');
          // This is a simple parsing - might need improvement for complex addresses
          const zipMatch = bankingInfo.branchAddress.match(/\b\d{5}\b$/);
          if (zipMatch) {
            parsedZip = zipMatch[0];
            const addressWithoutZip = bankingInfo.branchAddress.replace(/\s*\b\d{5}\b$/, '');
            const stateMatch = addressWithoutZip.match(/, ([A-Z]{2})$/);
            if (stateMatch) {
              parsedState = stateMatch[1];
              const addressWithoutState = addressWithoutZip.replace(/, [A-Z]{2}$/, '');
              const cityMatch = addressWithoutState.match(/,\s*([^,]+)$/);
              if (cityMatch) {
                parsedCity = cityMatch[1];
                parsedStreet = addressWithoutState.replace(/,\s*[^,]+$/, '');
              }
            }
          }
        }

        // Parse notes back into components if they were combined
        let parsedSpecialInstructions = '', parsedNotes = '';
        if (bankingInfo.notes) {
          const notesParts = bankingInfo.notes.split(' | ');
          if (notesParts.length === 2) {
            parsedSpecialInstructions = notesParts[0];
            parsedNotes = notesParts[1];
          } else {
            parsedNotes = bankingInfo.notes;
          }
        }

        setFormData({
          bankName: bankingInfo.bankName || '',
          accountType: bankingInfo.accountType || '',
          accountNumber: '',
          branchAddress: bankingInfo.branchAddress || '',
          branchPhone: formatPhoneNumber(bankingInfo.branchPhone || ''),
          onlineBankingUrl: bankingInfo.onlineBankingUrl || '',
          username: '',
          password: '',
          contactPerson: bankingInfo.contactPerson || '',
          contactPhone: formatPhoneNumber(bankingInfo.contactPhone || ''),
          notes: parsedNotes,
          location: '',
          specialInstructions: parsedSpecialInstructions,
          street: parsedStreet,
          city: parsedCity,
          state: parsedState,
          zip: parsedZip,
        });

        logger.debug('[Edit Debug] Form data set with parsed address:', {
          street: parsedStreet,
          city: parsedCity,
          state: parsedState,
          zip: parsedZip
        });

        // Load vault credentials
        try {
          logger.debug('[Edit Debug] Loading vault credentials for banking ID:', bankingId);
          const vaultData = await vaultService.getPortalCredentials(bankingId!, 'banking');
          logger.debug('[Edit Debug] Vault data loaded:', vaultData);
          
          if (vaultData && (vaultData.accountNumber || vaultData.username || vaultData.password)) {
            setHasOnlinePortal(true);
            setFormData(prev => ({
              ...prev,
              accountNumber: vaultData.accountNumber || '',
              username: vaultData.username || '',
              password: vaultData.password || '',
            }));
            logger.debug('[Edit Debug] Vault credentials set, hasOnlinePortal set to true');
          } else {
            logger.debug('[Edit Debug] No vault credentials found');
          }
        } catch (error) {
          logger.error('[Edit Debug] Error loading vault data:', error);
        }
      }
    } catch (error) {
      logger.error('Error loading banking data:', error);
      Alert.alert('Error', 'Failed to load banking information');
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.bankName.trim()) {
      Alert.alert('Validation Error', 'Please enter the bank name.');
      return;
    }

    if (!formData.accountType.trim()) {
      Alert.alert('Validation Error', 'Please enter the account type.');
      return;
    }

    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((hasOnlinePortal && (formData.accountNumber || formData.username || formData.password)) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }

      // Build address from components
      const fullAddress = [
        formData.street?.trim(),
        [formData.city?.trim(), formData.state?.trim()].filter(Boolean).join(', '),
        formData.zip?.trim(),
      ]
        .filter(Boolean)
        .join(' ');

      const bankingData = {
        bankName: formData.bankName.trim(),
        accountType: formData.accountType.trim(),
        branchAddress: fullAddress || formData.branchAddress?.trim(),
        branchPhone: formData.branchPhone.replace(/\D/g, ''),
        onlineBankingUrl: formData.onlineBankingUrl.trim(),
        contactPerson: formData.contactPerson.trim(),
        contactPhone: formData.contactPhone.replace(/\D/g, ''),
        notes: [formData.specialInstructions?.trim(), formData.notes?.trim()].filter(Boolean).join(' | '),
      };

      let savedBanking;
      if (isEdit && bankingId) {
        savedBanking = await bankingInfoService.updateBankingInfo(bankingId, bankingData);
      } else {
        savedBanking = await bankingInfoService.createBankingInfo(bankingData);
      }

      // Handle wrapped response format { success, data, message }
      const savedBankingData = savedBanking?.data || savedBanking;

      // Store the saved ID for the Done button
      setSavedBankingId(savedBankingData.id);

      // Store vault credentials if portal is enabled
      if (hasOnlinePortal && (formData.accountNumber || formData.username || formData.password)) {
        await vaultService.storePortalCredentials(savedBankingData.id, 'banking', {
          accountNumber: formData.accountNumber,
          username: formData.username,
          password: formData.password,
        });
      }

      setShowAddAnotherDone(true);
    } catch (error) {
      logger.error('Error saving banking info:', error);
      Alert.alert('Error', 'Failed to save banking information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Reset form and hide buttons
    setFormData({
      bankName: '',
      accountType: '',
      accountNumber: '',
      branchAddress: '',
      branchPhone: '',
      onlineBankingUrl: '',
      username: '',
      password: '',
      contactPerson: '',
      contactPhone: '',
      notes: '',
      location: '',
      specialInstructions: '',
      street: '',
      city: '',
      state: '',
      zip: '',
    });
    setHasOnlinePortal(false);
    setShowAccountNumber(false);
    setShowUsername(false);
    setShowPassword(false);
    setShowAddAnotherDone(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    const idToUse = savedBankingId || bankingId;
    logger.debug('[Done Debug] Using ID:', idToUse, 'savedBankingId:', savedBankingId, 'bankingId:', bankingId);
    (navigation as any).navigate('BankingInfoLockUnlock', { bankingId: idToUse });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit' : 'Add'} Banking Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={true}
        >
          
          {/* Bank Name */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Bank Name</Text>
            <TextInput
              style={styles.input}
              value={formData.bankName}
              onChangeText={(text) => setFormData({ ...formData, bankName: text })}
              placeholder="Enter Bank Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Account Type */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Type</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowAccountTypeModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.accountType && { color: '#A9A9A9' }
              ]}>
                {formData.accountType || 'Select Account Type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          {/* Branch Phone */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Branch Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.branchPhone}
              onChangeText={(text) => setFormData({ ...formData, branchPhone: formatPhoneNumber(text) })}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          {/* Contact Person */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contact Person</Text>
            <TextInput
              style={styles.input}
              value={formData.contactPerson}
              onChangeText={(text) => setFormData({ ...formData, contactPerson: text })}
              placeholder="Enter Contact Person Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Contact Phone */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contact Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.contactPhone}
              onChangeText={(text) => setFormData({ ...formData, contactPhone: formatPhoneNumber(text) })}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Does this bank have an online portal?</Text>
              <View style={styles.toggleWrapper}>
                <TouchableOpacity
                  style={[styles.toggleButton, hasOnlinePortal && styles.toggleButtonActive]}
                  onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
                >
                  <View style={[styles.toggleCircle, hasOnlinePortal && styles.toggleCircleActive]} />
                </TouchableOpacity>
                <Text style={styles.toggleText}>{hasOnlinePortal ? 'Yes' : 'No'}</Text>
              </View>
            </View>
          </View>

          {hasOnlinePortal && (
            <>
              {/* Website */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Website Address</Text>
                <TextInput
                  style={styles.input}
                  value={formData.onlineBankingUrl}
                  onChangeText={(text) => setFormData({ ...formData, onlineBankingUrl: text })}
                  placeholder="Enter Portal Website URL"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="url"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              {/* Account Number */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Account Number 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.accountNumber}
                    onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                    placeholder="Enter Account Number"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showAccountNumber}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowAccountNumber(!showAccountNumber)}
                  >
                    <Image
                      source={showAccountNumber ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')
                      }
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              {/* Username */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')
                      }
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              {/* Password */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                    textContentType="password"
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')
                      }
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          {/* Street Address */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Street Address</Text>
            <TextInput
              style={styles.input}
              value={formData.street}
              onChangeText={(text) => setFormData({ ...formData, street: text })}
              placeholder="Enter Street Address"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* City */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>City</Text>
            <TextInput
              style={styles.input}
              value={formData.city}
              onChangeText={(text) => setFormData({ ...formData, city: text })}
              placeholder="Enter City"
              placeholderTextColor="#A9A9A9"
              autoCapitalize="words"
            />
          </View>

          {/* State */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>State</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowStateModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.state && { color: '#A9A9A9' }
              ]}>
                {formData.state || 'Select State'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          {/* Zip Code */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Zip Code</Text>
            <TextInput
              style={styles.input}
              value={formData.zip}
              onChangeText={(text) => setFormData({ ...formData, zip: text })}
              placeholder="Enter Zip Code"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={5}
            />
          </View>

          {/* Where do you keep your banking information? */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Where do you keep your banking information?</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => setShowLocationModal(true)}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.location && { color: '#A9A9A9' }
              ]}>
                {formData.location || 'Select Location'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          {/* Special Instructions */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.specialInstructions}
              onChangeText={(text) => setFormData({ ...formData, specialInstructions: text })}
              placeholder="Enter any Special Instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {/* Submit Button or Add Another/Done */}
          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={[styles.submitButton, styles.addAnotherButton]} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.submitButton, styles.doneButton]} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.submitButton, submitting && { opacity: 0.7 }]}
                onPress={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                )}
              </TouchableOpacity>
            </View>
          )}

        </ScrollView>
      </View>

      {/* Account Type Modal */}
      <Modal
        visible={showAccountTypeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowAccountTypeModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowAccountTypeModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Account Type</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowAccountTypeModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {ACCOUNT_TYPES.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => {
                    setFormData({ ...formData, accountType: type });
                    setShowAccountTypeModal(false);
                  }}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Location Modal */}
      <Modal
        visible={showLocationModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowLocationModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowLocationModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Location</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowLocationModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {LOCATIONS.map((location) => (
                <TouchableOpacity
                  key={location}
                  style={styles.stateItem}
                  onPress={() => {
                    setFormData({ ...formData, location });
                    setShowLocationModal(false);
                  }}
                >
                  <Text style={styles.stateItemText}>{location}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* State Modal */}
      <Modal
        visible={showStateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStateModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowStateModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.stateItem}
                  onPress={() => {
                    setFormData({ ...formData, state });
                    setShowStateModal(false);
                  }}
                >
                  <Text style={styles.stateItemText}>{state}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  vaultText: {
    fontSize: ms(12, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontStyle: 'italic',
    marginLeft: ms(2),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputWithEye: {
    paddingRight: ms(50),
  },
  eyeIcon: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: ms(5),
    zIndex: 1,
  },
  eyeIconImage: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
    gap: ms(15),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F64',
    flex: 1,
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    flex: 1,
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(8),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    flex: 1,
  },
  toggleWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(12),
  },
  toggleButton: {
    width: s(60),
    height: s(32),
    backgroundColor: '#E5E7EB',
    borderRadius: ms(16),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#FB923C',
  },
  toggleCircle: {
    width: s(28),
    height: s(28),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    alignSelf: 'flex-start',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toggleCircleActive: {
    alignSelf: 'flex-end',
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    minWidth: s(30),
  },
});